import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import Carousel from 'aquilla/core/molecules/Carousel';
import { Fonts, Spacings, mediaQuery } from '@styles/index';
import H2 from '@base/H2/H2';
import H4 from '@base/H4/H4';
import H5 from '@base/H5/H5';
import P1 from '@base/P1/P1';
import P2 from '@base/P2/P2';
import Button from '@base/Button/Button';
import { getImage } from '@utils/imageHelper';
import { fullDateWithTime } from '@utils/dates';
import { GTM_ACTIVITY_CLICKED } from '@constants/segment';
import { sendEvents } from '@events/sendAnalytics';
import { segment } from '@actions/analytics';
import Play from '@icon/Play';
import useGateSuggestions from '@api/hooks/rank-predictor/useGateSuggestions';

const Wrapper = styled.div`
  padding-top: ${Spacings.SPACING_15B};
  padding-bottom: ${Spacings.SPACING_15B};
  padding-left: 13vw;
  padding-right: 13vw;
  background: var(--color-base-1);
  text-align: left;
  display: flex;
  column-gap: ${Spacings.SPACING_17B};

  ${mediaQuery.uptoTablet} {
    padding: ${Spacings.SPACING_4B};
    padding-top: ${Spacings.SPACING_9B};
    flex-direction: column;
    text-align: center;
  }
`;

const LeftWrapper = styled.div`
  flex: 25%;
  max-width: 296px;
  ${mediaQuery.uptoTablet} {
    flex: 100%;
    max-width: unset;
  }
`;

const BulbImage = styled.img`
  height: ${Spacings.SPACING_12B};
  width: ${Spacings.SPACING_16B};
  margin-bottom: ${Spacings.SPACING_1B};
`;

const StreamOptions = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_16B};
  max-width: 269px;
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_10B};
    max-width: unset;
    flex-direction: row;
    column-gap: ${Spacings.SPACING_2B};
    overflow: scroll;
    overflow-y: hidden;
    scrollbar-width: none;

    ::-webkit-scrollbar {
      width: 0;
      background: transparent;
    }

    ::-webkit-scrollbar-thumb {
      background: transparent;
    }
  }
`;

const StreamOption = styled.div`
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_5B};
  background: ${({ $selected }) =>
    $selected ? 'var(--color-i-white)' : 'rgba(240, 244, 247, 0.4)'};
  box-shadow: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B}
    ${Spacings.SPACING_8B} rgba(221, 230, 237, 0.4);
  border-radius: ${Spacings.SPACING_2B};
  cursor: pointer;
  white-space: nowrap;
  ${mediaQuery.uptoTablet} {
    min-width: 104px;
    h4 {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

const StyH4 = styled(H4)`
  ${({ $selected }) => ($selected ? '' : 'color: var(--color-i-gray-3)')}
`;

const RightWrapper = styled.div`
  flex: 75%;
  max-width: 70%;
  ${mediaQuery.uptoTablet} {
    max-width: unset;
    flex: 100%;
  }
`;

const ClassCardWrapper = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_10B};
  }
  & > div:first-child {
    width: 52vw;
    ${mediaQuery.uptoTablet} {
      width: 100vw;
      margin-bottom: -${Spacings.SPACING_12B};
      margin-left: -${Spacings.SPACING_4B};
    }
  }
`;

const CardWrapper = styled.div`
  width: 368px;
  background: var(--color-i-white);
  box-shadow: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B}
    ${Spacings.SPACING_8B} var(--color-i-gray-v2);
  border-radius: ${Spacings.SPACING_10};
  margin-right: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoTablet} {
    width: 328px;
    text-align: left;
  }
`;

const CardHeader = styled.div`
  position: relative;
  border-top-left-radius: ${Spacings.SPACING_10};
  border-top-right-radius: ${Spacings.SPACING_10};
  background: linear-gradient(
    270deg,
    rgba(150, 194, 255, 0.43) 0%,
    rgba(150, 194, 255, 0) 65.49%
  );
  padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_5B} ${Spacings.SPACING_7B};
`;

const HeaderImage = styled.img`
  height: 145px;
  width: 145px;
  top: ${Spacings.SPACING_0B};
  right: ${Spacings.SPACING_0B};
  position: absolute;
  border-radius: ${Spacings.SPACING_2B};
`;

const StreamLabel = styled(P1)`
  color: var(--color-i-blue-7);
  font-weight: ${Fonts.SEMIBOLD};
`;

const StyH5 = styled(H5)`
  margin-top: ${Spacings.SPACING_1B};
`;

const ExamDuration = styled(P1)`
  margin-top: ${Spacings.SPACING_4B};
`;

const CardDescriptionWrapper = styled.div`
  padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_5B};
  height: 144px;
`;

const CardDescription = styled(P2)`
  color: var(--color-text-secondary);
`;

const Divider = styled.hr`
  width: 90%;
  border-top: 1px solid var(--color-divider);
  margin: 0 auto;
`;

const TimeAndLink = styled.div`
  display: flex;
  padding: ${Spacings.SPACING_5B} ${Spacings.SPACING_5B} ${Spacings.SPACING_6B};
  align-items: center;
  justify-content: space-between;
`;

const ClassTime = styled(P1)``;

const ClassCard = ({ classInfo }) => {
  const {
    streamName,
    examName,
    educatorName,
    examDuration,
    description,
    createdAt,
    classLink
  } = classInfo;

  const onClassClick = () => {
    const event = {
      goal_name: 'gate-ese',
      goal_uid: 'PESHE',
      last_primary_source: 'NA',
      last_primary_source_section: 'NA',
      current_screen: 'Rank Predictor'
    };
    sendEvents(GTM_ACTIVITY_CLICKED, event);
    segment.track(GTM_ACTIVITY_CLICKED, event);
    if (classLink) window.open(classLink, '_blank');
  };
  return (
    <CardWrapper>
      <CardHeader>
        <HeaderImage src={getImage('rankpredictor/play-lines-bg.svg')} />
        <StreamLabel>{streamName}</StreamLabel>
        <StyH5>{examName}</StyH5>
        <P1>By {educatorName}</P1>
        <ExamDuration>{examDuration}</ExamDuration>
      </CardHeader>
      <CardDescriptionWrapper>
        <CardDescription>{description}</CardDescription>
      </CardDescriptionWrapper>
      <Divider />
      <TimeAndLink>
        <ClassTime>{fullDateWithTime(createdAt)}</ClassTime>
        {classLink && (
          <Button
            label="Watch Now"
            grid={3}
            icon={
              <Play size={Spacings.SPACING_4B} color="var(--color-i-white)" />
            }
            onClick={onClassClick}
          />
        )}
      </TimeAndLink>
    </CardWrapper>
  );
};

const GateSolutions = () => {
  const { gateSuggestionData: gateSolutions } = useGateSuggestions();
  const [selectedStream, setSelectedStream] = useState({});
  const [selectedStreamData, setSelectedStreamData] = useState([]);

  useEffect(() => {
    if (gateSolutions?.length) {
      setSelectedStream({
        id: gateSolutions[0].id,
        streamTitle: gateSolutions[0].streamTitle
      });
      setSelectedStreamData(gateSolutions[0].data);
    }
  }, [gateSolutions]);

  const onStreamSelection = (solution) => {
    if (selectedStream.id !== solution.id) {
      setSelectedStream({
        id: solution.id,
        streamTitle: solution.streamTitle
      });
      const selectedSolution = gateSolutions.find(
        (gateSolution) => gateSolution.id === solution.id
      );
      setSelectedStreamData(selectedSolution.data);
    }
  };

  if (!gateSolutions?.length) return null;

  return (
    <Wrapper>
      <LeftWrapper>
        <BulbImage
          src={getImage('rankpredictor/light-blub.svg')}
          alt="light-blub"
        />
        <H2>View the complete solution</H2>
        <StreamOptions>
          {gateSolutions.map((solution) => (
            <StreamOption
              key={solution.id}
              onClick={() => onStreamSelection(solution)}
              $selected={solution.id === selectedStream.id}
            >
              <StyH4 $selected={solution.id === selectedStream.id}>
                {solution.streamTitle}
              </StyH4>
            </StreamOption>
          ))}
        </StreamOptions>
      </LeftWrapper>
      <RightWrapper>
        <ClassCardWrapper>
          <Carousel itemsPerSlide={2} controls={{ showArrows: true }}>
            {selectedStreamData?.map((classInfo) => (
              <ClassCard key={classInfo.id} classInfo={classInfo} />
            ))}
          </Carousel>
        </ClassCardWrapper>
      </RightWrapper>
    </Wrapper>
  );
};

export default GateSolutions;
